<script lang="ts" setup>
  import { computed, type ComputedRef, inject } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { BaseButton } from '@qic/ui-components'
  import CountryFlag from '@/shared/components/CountryFlag/CountryFlag.vue'
  import BaseChip from '@/shared/components/BaseChip/BaseChip.vue'
  import {
    POLICY_WORDING_CLICK_EVENT,
    SELECT_PLAN_EVENT_NAME,
    TOGGLE_EVENT_NAME,
  } from './FlowPlan.constants'
  import type { FlowPlanVariant } from '@/shared/types/components.types'
  import FlowAccordion from '@/entities/Flow/components/FlowAccordion/FlowAccordion.vue'
  import { PLAN_NAMES_HASH } from '@/shared/constants/plans.constants'
  import type { Plan } from '@/shared/types/api.types'
  import { TravelDirection } from '@/shared/types/common.types'
  import TicketChancesBlock from '@/shared/components/TicketChancesBlock/TicketChancesBlock.vue'

  interface Props {
    variant: FlowPlanVariant
    isRecomended?: boolean
    policyWording?: string
    plan: Plan
    typeOfTravel?: TravelDirection
    planTitle?: string
    dataTest?: string
  }

  interface Emits {
    (e: typeof TOGGLE_EVENT_NAME, isOpen: boolean, rowId: string, rowName: string): void

    (e: typeof SELECT_PLAN_EVENT_NAME): void

    (e: typeof POLICY_WORDING_CLICK_EVENT): void
  }

  const props = withDefaults(defineProps<Props>(), {
    variant: 'worldwide',
    isRecomended: false,
    typeOfTravel: TravelDirection.UNKNOWN,
    dataTest: 'flow-plan',
  })

  const emit = defineEmits<Emits>()

  const { t } = useI18n()

  const flagIso = computed(() => (props.variant === 'europe' ? 'eu-2' : 'global'))
  const title = computed(() => {
    if (props.planTitle) {
      return props.planTitle
    }

    if (props.variant === 'single') {
      return t('inbound_quote_inclusive_covers')
    }

    const translateKey = PLAN_NAMES_HASH[props.plan.schemecode]
    return translateKey ? t(translateKey) : props.plan.name
  })

  const handleSelectPlan = () => {
    emit(SELECT_PLAN_EVENT_NAME)
  }

  const handleToggle = (isOpen: boolean, rowId: string, rowName: string) => {
    emit(TOGGLE_EVENT_NAME, isOpen, rowId, rowName)
  }

  const selectButtonHtml = computed(() =>
    t('outbound_plan_select_button', {
      planPrice: `<span class="ltr">&nbsp;${t('qar')} ${props.plan.price}&nbsp;</span>`,
    })
  )

  const isRamadanCampaign = inject('isRamadanCampaign') as ComputedRef<boolean>
</script>

<template>
  <div>
    <div class="flow-plan" :class="{ 'flow-plan--recomended': props.isRecomended }">
      <FlowAccordion
        data-test="flow-plan"
        :plan="plan"
        :title="title"
        class="flow-plan__accordion"
        :with-price="props.variant === 'single'"
        :type-of-travel="typeOfTravel"
        @toggle="handleToggle"
      >
        <template #chips>
          <BaseChip v-if="props.isRecomended" variant="yellow" data-test="recommended-chip">
            {{ $t('outbound_plan_recommended') }}
          </BaseChip>
        </template>

        <template #before-title>
          <CountryFlag v-if="props.variant !== 'single'" class="flow-plan__icon" :iso="flagIso" />
        </template>

        <template #after-title>
          <slot name="after-title"></slot>
        </template>
      </FlowAccordion>

      <div v-if="props.policyWording" class="flow-plan__wording">
        <a
          :href="props.policyWording"
          target="_blank"
          rel="noopener noreferrer"
          :data-test="`${dataTest}-policy-wording-link`"
          @click="$emit(POLICY_WORDING_CLICK_EVENT)"
        >
          <slot name="policyWording">
            {{ $t('outbound.plans.plan_information.policy_wording') }}
          </slot>
        </a>
      </div>

      <div v-if="props.variant !== 'single'" class="flow-plan__footer">
        <template v-if="props.variant === 'worldwide'">
          {{ $t('outbound.plans.plan_information.addon_price_annotation') }}
        </template>
        <BaseButton
          class="flow-plan__footer-button"
          :secondary="!props.isRecomended"
          big
          icon="none"
          @click="handleSelectPlan"
          v-html="selectButtonHtml"
        />
        <TicketChancesBlock v-if="isRamadanCampaign" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import './FlowPlan';
</style>
